/**
 * The secret-state source (1.M-2 deliverable 9): the `EditorServices.subscribeSecretState` shape
 * over `secrets.forDoc` and `ledger.changed`. It fetches a page's secrets on subscribe and emits
 * their states (orphaned rows left out), and fetches again only on a `ledger.changed` that names
 * the page or a secret it emitted. A late answer older than a newer one is dropped; a failed fetch
 * emits nothing and the next event retries; nothing is emitted after the unsubscribe. It reads no
 * flag: 1.Y wires it into `adapter.ts` behind `ledger`.
 */
import { type SecretStateV1, type SecretV1, secretsForDoc } from '@codex/contracts';
import { api } from '../auth/client.js';
import { appEvents } from '../editor-services/events.js';

export interface LedgerChangedData {
  readonly secretIds: readonly string[];
  readonly docIds: readonly string[];
}

export interface SecretStateSourceDeps {
  /** One page's secrets (`secrets.forDoc`). */
  readonly fetchSecrets: (docId: string) => Promise<readonly SecretV1[]>;
  /** Every `ledger.changed`; returns the unsubscribe. */
  readonly subscribe: (listener: (data: LedgerChangedData) => void) => () => void;
}

export type SecretStateListener = (states: readonly SecretStateV1[]) => void;
export type SubscribeSecretStates = (docId: string, listener: SecretStateListener) => () => void;

/** The states the editor shows: one per secret still in a body. */
export function secretStates(secrets: readonly SecretV1[]): SecretStateV1[] {
  return secrets
    .filter((secret) => secret.orphanedAt === null)
    .map((secret) => ({ secretId: secret.id, label: secret.label ?? '', state: secret.state }));
}

export function createSecretStateSource(deps: SecretStateSourceDeps): SubscribeSecretStates {
  return (docId, listener) => {
    let active = true;
    let asked = 0;
    let shown = 0;
    let held = new Set<string>();

    const load = () => {
      asked += 1;
      const ticket = asked;
      deps.fetchSecrets(docId).then(
        (secrets) => {
          if (!active || ticket < shown) return;
          shown = ticket;
          const states = secretStates(secrets);
          held = new Set(states.map((s) => s.secretId));
          listener(states);
        },
        () => {
          // Nothing is emitted; the next event that names the page fetches again.
        },
      );
    };

    const unsubscribe = deps.subscribe((data) => {
      if (!active) return;
      if (data.docIds.includes(docId) || data.secretIds.some((id) => held.has(id))) load();
    });
    load();
    return () => {
      active = false;
      unsubscribe();
    };
  };
}

/** The app's instance, over `secrets.forDoc` and the shared event stream. */
export const subscribeSecretStates: SubscribeSecretStates = createSecretStateSource({
  fetchSecrets: async (docId) => (await api(secretsForDoc, { params: { id: docId } })).items,
  subscribe: (listener) => appEvents.subscribe('ledger.changed', (event) => listener(event.data)),
});
